import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useMemo } from "react";
import * as Yup from "yup";

import { Button, getInput } from "../../../../components/ui";
import { CREATE_CONSIGNMENT_JOB_REQUEST } from "../../../../graphql/mutation/Consignment";
import { FETCH_JOB_REQUESTS_V2 } from "../../../../graphql/query/Job";
import { FETCH_JOB_TYPES } from "../../../../graphql/query/JobType";
import { getJobTypeOptions, jobTypeStyles } from "../../../Jobs/jobTypeOptions";
import { useEntities } from "../../../../hooks/useEntities";
import useUser from "../../../../hooks/useUser";

const CreateJob = props => {
  const { closeModal, consignmentId } = props;
  const { user } = useUser();
  const { data: { jobTypes = [] } = {} } = useQuery(FETCH_JOB_TYPES);
  const [createConsignmentJob, { loading }] = useMutation(
    CREATE_CONSIGNMENT_JOB_REQUEST,
    {
      refetchQueries: [
        FETCH_JOB_REQUESTS_V2({
          jobStatus: {},
          jobType: {},
          requestUser: {},
          user: {},
          variables: {
            consignmentId: {
              value: consignmentId,
              operator: "eq",
            },
          },
        }),
      ],
    },
  );
  const { entities: pickupGroups } = useEntities({
    variables: { input: { hide: 0, user: 0, order: ["name"] } },
  });

  const formik = useFormik({
    initialValues: { statusId: 1, date: new Date(), consignmentId },
    validationSchema: Yup.object({
      typeId: Yup.string().trim().required("You must select type for new job."),
      description: Yup.string()
        .trim()
        .required("You must enter description for new job."),
      dueDate: Yup.date()
        .required("You must select due date for new job.")
        .test(
          "validation",
          `Due-Date must be greater than or equal to tomorrow's date`,
          function (value) {
            return new Date(value) > new Date();
          },
        ),
      duetime: Yup.string()
        .required("Due-Time must be greater than or equal to 10 AM GMT")
        .test(
          "validation",
          `Due-Time must be greater than or equal to 10 AM GMT`,
          function (value) {
            const splitTime = value.split(":");
            return Number(splitTime[0]) >= 10;
          },
        ),
      pickupId: Yup.number()
        .typeError("You must enter pickup for new job.")
        .required("You must enter pickup for new job."),
    }),
    onSubmit: async (values, { setErrors }) => {
      const error = {};
      const updatedDueTime = new Date(`${values.dueDate} ${values.duetime}`);

      if (!(values?.duetime.split(":")[0] >= 10)) {
        error.duetime = `Due-Time must be greater than or equal to 10 AM GMT`;
        setErrors(error);
        return;
      }

      const response = await createConsignmentJob({
        variables: {
          input: {
            ...values,
            requestUserId: user?.entityId,
            duetime: updatedDueTime,
          },
        },
      });

      if (response?.data?.createConsignmentJob?.success) {
        formik.resetForm();
        closeModal();
      }
    },
  });

  const inputs = [
    {
      label: "Type",
      name: "typeId",
      inputId: "typeId",
      type: "multi-select",
      placeholder: "Select a type",
      options: getJobTypeOptions(jobTypes),
      styles: jobTypeStyles,
    },
    { label: "Description", name: "description", type: "text" },
    { label: "Due Date", name: "dueDate", type: "date" },
    {
      label: "Due Time",
      name: "duetime",
      type: "time",
      disabled: !formik.getFieldProps("dueDate").value,
    },
    {
      label: "Pickup Group",
      name: "pickupId",
      type: "multi-select",
      options:
        pickupGroups?.edges?.map(item => ({
          label: item?.name,
          value: item?.id,
        })) || [],
    },
  ];

  const hasErrors = useMemo(
    () => Object.keys(formik?.errors || {}).length > 0,
    [formik?.errors],
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-row">
        <div className="flex flex-1 flex-col text-2xl font-bold">
          <div>Create Job for all Consignment Works</div>
        </div>
        <div className="flex gap-4">
          <Button
            action="default"
            label="Cancel"
            onClick={() => {
              closeModal();
              formik.resetForm();
            }}
          />
          <Button
            label={"Create"}
            loading={loading}
            disabled={loading || hasErrors}
            onClick={formik.submitForm}
          />
        </div>
      </div>
      <div className="w-50 grid grid-cols-1 gap-4 md:grid-cols-2">
        {inputs?.map((item, index) => {
          const inputProps = {
            ...item,
            formik,
          };
          return <div key={index}>{getInput(inputProps)}</div>;
        })}
      </div>
    </div>
  );
};

export default CreateJob;
